import Link from "next/link";
import DeliveryMethodsSection from "../shipping/shipSelector.comp";
import { useEffect, useMemo, useState } from "react";
import { Cart, CartFormData, CartFormDataItem } from "../../types/cart";
import { useTranslation } from "../../core/translation.core";
import { useCookies, useIsLogin } from "../../core/cookie.core";
import useSelectShipOption from "../../hooks/services/useSelectShipOption";
import useIneligibleFreeShipOption from "../../hooks/services/useIneligibleFreeShipOption";
import { formatCurrency } from "../../core/currency.core";
import { useForm, useWatch, UseFormReturn } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
  TempCheckoutSessionProvider,
  useTempCheckoutSessionContext,
} from "./useTempCheckoutSession.hook";
import ApplyGiftCards from "../checkout/applyGiftCards.comp";
import ApplyGuruDollar from "../checkout/applyGuruDollar.comp";
import checkoutCore from "../../core/checkout.core";
import { LoadingComp } from "../common/loading.comp";
import { useCart } from "../../hooks/cart/useCart.hook";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import useShop from "../common/useShop.hook";
import Cookies from "js-cookie";
import useGetShipOptions from "./useGetShipOptions.hook";
import productCore from "../../core/product.core";
import { PointRedeemErrorMessage, PointRedeemSummaryRow } from "./pointRedemptionDisplay.comp";
import { sumCartCashSubtotal } from "../../core/pointRedemption.core";
import { checkoutSessionQueryKeys, syncCheckoutSessionCachesIfSameId } from "../../hooks/cart/checkoutSessionCache";
import { CheckoutSession } from "../../types/checkout";
import { useCartStockStatus } from "../../hooks/useCartStockStatus.hook";

export default function CartOrderSummary() {
  const isLogin = useIsLogin();
  const { t, msg } = useTranslation();
  const methods = useForm<CartFormData>();
  const { cart, cartTotal } = useCart();
  const shop = useShop();

  const cartItemsKey = useMemo(() => cart?.items.map((item) => `${item.id}:${item.quantity}`).join(",") ?? "", [cart?.items]);

  useEffect(() => {
    if (cartTotal && cartTotal <= (shop?.minOrderAmtAfterDeduct ?? 1)) {
      toast.error(t(msg.cart.totalTooLowError));
      return;
    }
    if (cart && cart.items.length > 0) {
      const formData = convertCartToFormData(cart);
      methods.reset(formData);
    }
    // eslint-disable-next-line
  }, [cartItemsKey, cart, methods]);

  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });

  if (!cart) return null;

  // The session provider must be fed from the cart itself, not from the form watch:
  // when the cart arrives synchronously from the react-query cache (SPA navigation),
  // methods.reset() runs before this component's useWatch subscribes, so the watch
  // stays empty forever and the temp session is never created (greyed ship options).
  const providerCartItems = convertCartToFormData(cart).items;

  const isAllVirtualItems = productCore.isAllVirtualItems(cart.items);

  return (
    <TempCheckoutSessionProvider cartItems={providerCartItems as CartFormDataItem[]}>
      <div className="lg:w-1/3">
        <div className="bg-white rounded-lg">
          {isLogin ? <DeliveryMethodsSection methods={methods} /> : null}

          <div className="p-5 space-y-4">
            {!isAllVirtualItems ? (
              <>
                <CartApplyGuruDollar />
                <ApplyGiftCards isTemp />
              </>
            ) : null}
            <CartOrderSubtotal />
            <CartOrderShippingFee methods={methods} />
            <CheckoutDiscounts />
            <CartOrderTotal methods={methods} />
            {isLogin ? <ShipSelectionMessages methods={methods} /> : null}

            {isLogin ? (
              <CartCheckoutButton methods={methods} />
            ) : (
              <Link
                href="/auth?page=login&checkingOut=true"
                id="log-or-reg-link"
                className="block w-full mt-5 text-center bg-gray-100 text-gray-800 font-medium py-3 rounded hover:bg-gray-200 transition-colors text-base"
              >
                {t(msg.menu.accountState)}
              </Link>
            )}
          </div>
        </div>
      </div>
    </TempCheckoutSessionProvider>
  );
}

function CartApplyGuruDollar() {
  const { calculatedTotal } = useTempCheckoutSessionContext();

  return <ApplyGuruDollar isTemp orderTotal={calculatedTotal} />;
}

function CartCheckoutButton({ methods }: { methods: UseFormReturn<CartFormData> }) {
  const [isLoading, setIsLoading] = useState(false);
  const isLogin = useIsLogin();
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });
  const { cart, cartTotal, isLoading: cartIsLoading } = useCart();
  const router = useRouter();
  const shop = useShop();
  const tempSessionId = useCookies("tempSessionId");
  const { checkoutSession: tempCheckoutSession, isLoading: sessionLoading, pointRedeemError } = useTempCheckoutSessionContext();
  const pendingGiftSelections = checkoutCore.countPendingGiftGroupSelections(tempCheckoutSession?.giftPromotions);
  const { hasStockIssue, isLoading: stockStatusLoading } = useCartStockStatus(cart ?? undefined);
  const { data: { deliveryOptions } = {} } = useGetShipOptions(selectedCartItems as CartFormDataItem[], cartIsLoading);
  const { selectedShipOption } = useSelectShipOption({
    deliveryOptions,
    methods,
    items: cart?.items ?? [],
    isTemp: true,
  });

  async function checkout() {
    if (!isLogin) {
      router.push({
        pathname: "/auth",
        query: {
          page: "login",
          redirectTo: "/cart",
          checkingOut: "true",
        },
      });
      return;
    }

    if (!cart || cart.items.length === 0) {
      toast.error(t(msg.product.cartIsEmpty));
      return;
    }

    if (cartTotal && cartTotal <= (shop?.minOrderAmtAfterDeduct ?? 1)) {
      toast.error(t(msg.cart.totalTooLowError));
      return;
    }

    const formData = methods.getValues();
    if (!formData.items?.length) {
      return toast.error(t(msg.product.cartIsEmpty));
    }

    setIsLoading(true);

    try {
      if (!tempSessionId) {
        setIsLoading(false);
        return toast.error(t(msg.errors.contactAdmin));
      }

      Cookies.set("sessionId", tempSessionId);

      const tempSession = queryClient.getQueryData<CheckoutSession>(checkoutSessionQueryKeys.temp);
      if (tempSession) {
        syncCheckoutSessionCachesIfSameId(queryClient, tempSession);
      }

      router.push("/checkout");
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(t(msg.general.errSubmit));
      setIsLoading(false);
    }
  }

  function areAllSelectedItemsGiftCards(): boolean {
    const selected = (selectedCartItems as CartFormDataItem[]).filter((item) => item.selected);

    return (
      selected.length > 0 &&
      selected.every((item) => {
        const cartItem = cart!.items.find((ci) => ci.id === item.id);
        return cartItem?.product.productType === "giftCard";
      })
    );
  }

  function isCheckoutButtonEnabled() {
    if (isLoading) return false;
    if (cartIsLoading) return false;
    if (sessionLoading) return false;
    if (stockStatusLoading) return false;
    if (pointRedeemError) return false;
    if (hasStockIssue) return false;
    if (pendingGiftSelections > 0) return false;

    if (!areAllSelectedItemsGiftCards() && !selectedShipOption) {
      return false;
    }

    return true;
  }

  return (
    <>
      <PointRedeemErrorMessage errorCode={pointRedeemError} />
      {hasStockIssue && !stockStatusLoading ? (
        <p className="text-sm text-red-600">{t(msg.cart.checkoutBlockedOutOfStock)}</p>
      ) : null}
      {pendingGiftSelections > 0 ? (
        <p className="text-sm text-red-600">{t(msg.cart.giftSelectionRequired)}</p>
      ) : null}
      <button
      className="w-full mt-5 bg-primary text-white font-medium py-3 rounded bg-primary-light transition-colors text-base disabled:bg-gray-200"
      onClick={checkout}
      disabled={!isCheckoutButtonEnabled()}
      id="checkout-link"
    >
      {isLoading ? t(msg.general.loading) : t(msg.checkout.checkout)}
    </button>
    </>
  );
}

function CartOrderSubtotal() {
  const { t, msg } = useTranslation();
  const { cart } = useCart();
  const { calculatedTotal } = useTempCheckoutSessionContext();

  const currentSubtotal = useMemo(() => {
    if (!cart) return 0;
    return sumCartCashSubtotal(cart.items);
  }, [cart]);

  return (
    <div className="flex justify-between text-base pt-4 border-t border-gray-100">
      <span className="text-gray-700">{t(msg.cart.subtotal)}</span>
      <span className="font-medium">{formatCurrency(calculatedTotal?.itemsTotal ?? currentSubtotal, "HKD")}</span>
    </div>
  );
}

function CartOrderShippingFee({ methods }: { methods: UseFormReturn<CartFormData> }) {
  const { t, msg } = useTranslation();
  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });
  const { cart, isLoading: cartIsLoading } = useCart();
  const { calculatedTotal, isTotalLoading } = useTempCheckoutSessionContext();
  const { data: { deliveryOptions } = {} } = useGetShipOptions(selectedCartItems as CartFormDataItem[], cartIsLoading);
  const { selectedShipOption } = useSelectShipOption({
    deliveryOptions,
    methods,
    items: cart?.items ?? [],
    isTemp: true,
  });

  const shippingFee = calculatedTotal ? calculatedTotal.shipping : selectedShipOption?.fee ?? 0;
  const shippingExtra = calculatedTotal?.shippingExtra ?? 0;
  const currency = calculatedTotal?.currency ?? "HKD";

  if (isTotalLoading && !calculatedTotal) {
    return (
      <div className="flex justify-between text-base">
        <span className="text-gray-700">{t(msg?.order?.shippingFee)}</span>
        <LoadingComp sizeClassName="h-4 w-4" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between text-base">
        <span className="text-gray-700">{t(msg?.order?.shippingFee)}</span>
        <span className="font-medium">{shippingFee > 0 ? formatCurrency(shippingFee, currency) : t(msg.cart.free)}</span>
      </div>
      {shippingExtra > 0 ? (
        <div className="flex justify-between text-base">
          <span className="text-gray-700">{t(msg.checkout.shippingExtra)}</span>
          <span className="font-medium">{formatCurrency(shippingExtra, currency)}</span>
        </div>
      ) : null}
    </>
  );
}

function CartOrderTotal({ methods }: { methods: UseFormReturn<CartFormData> }) {
  const { t, msg } = useTranslation();
  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });
  const { cart, isLoading: cartIsLoading } = useCart();
  const { calculatedTotal } = useTempCheckoutSessionContext();
  const { data: { deliveryOptions } = {} } = useGetShipOptions(selectedCartItems as CartFormDataItem[], cartIsLoading);
  const { selectedShipOption } = useSelectShipOption({
    deliveryOptions,
    methods,
    items: cart?.items ?? [],
    isTemp: true,
  });

  const currentSubtotal = useMemo(() => {
    if (!cart) return 0;
    return sumCartCashSubtotal(cart.items);
  }, [cart]);

  const currentTotal = useMemo(() => {
    return currentSubtotal + (selectedShipOption?.fee ?? 0);
  }, [currentSubtotal, selectedShipOption]);

  return (
    <div className="flex justify-between text-lg font-medium pt-4 border-t border-gray-100 pb-2">
      <span>{t(msg?.order?.totalL)}</span>
      <span>{formatCurrency(calculatedTotal?.total ?? currentTotal, calculatedTotal?.currency ?? "HKD")}</span>
    </div>
  );
}

function ShipSelectionMessages({ methods }: { methods: UseFormReturn<CartFormData> }) {
  const { t, msg } = useTranslation();
  const { items: selectedCartItems = [] } = useWatch({ control: methods.control });
  const { cart, isLoading: cartIsLoading } = useCart();
  const { calculatedTotal } = useTempCheckoutSessionContext();
  const { data: { deliveryOptions, shippingFees } = {} } = useGetShipOptions(
    selectedCartItems as CartFormDataItem[],
    cartIsLoading
  );
  const { selectedShipOption } = useSelectShipOption({
    deliveryOptions,
    methods,
    items: cart?.items ?? [],
    isTemp: true,
  });
  const freeOptionAmtAbove = useIneligibleFreeShipOption(selectedShipOption, shippingFees) ?? 0;

  const actualShippingCost = calculatedTotal
    ? (calculatedTotal.shipping ?? 0) + (calculatedTotal.shippingExtra ?? 0)
    : selectedShipOption?.fee ?? 0;

  if (!selectedShipOption) {
    return (
      <div className="mt-4 bg-rose-50 rounded-lg p-4">
        <p className="text-sm text-gray-700 text-center">{t(msg?.delivery?.paragraph0)}</p>
      </div>
    );
  }

  if (actualShippingCost === 0) {
    return (
      <div className="bg-green-50 border border-green-100 rounded-lg">
        <div className="flex items-start gap-3 p-3">
          <span className="text-green-500 font-medium mt-0.5">✓</span>
          <span className="text-base">{t(msg?.delivery?.paragraph13)}</span>
        </div>
      </div>
    );
  }

  if (freeOptionAmtAbove) {
    return null;
    // return (
    //   <div className="mt-4 space-y-3">
    //     <div className="mt-4 bg-rose-50 rounded-lg p-4">
    //       <p className="text-sm text-gray-700">
    //         {t(msg?.delivery?.paragraph14).replace("{{freeAmtAbove}}", formatCurrency(freeOptionAmtAbove, "HKD")!)}
    //       </p>
    //     </div>

    //     <div className="bg-yellow-50 border border-yellow-100 rounded-lg">
    //       <div className="flex items-start gap-3 p-3">
    //         <span className={`mt-0.5  text-red-500 font-medium`}>×</span>
    //         <span className="text-base">
    //           {t(msg?.delivery?.paragraph15).replace(/{{freeAmtAbove}}|{{deliveryFee}}/g, (match) => {
    //             switch (match) {
    //               case "{{freeAmtAbove}}":
    //                 return formatCurrency(freeOptionAmtAbove, "HKD")!;

    //               default:
    //                 return formatCurrency(selectedShipOption.fee, "HKD")!;
    //             }
    //           })}
    //         </span>
    //       </div>
    //     </div>
    //   </div>
    // );
  }

  return null;
}

function CheckoutDiscounts() {
  const { t, msg } = useTranslation();
  const { checkoutSession, calculatedTotal, isTotalLoading } = useTempCheckoutSessionContext();

  if (isTotalLoading) {
    return (
      <div className="py-5">
        <LoadingComp sizeClassName="h-6 w-6" />
      </div>
    );
  }

  if (!calculatedTotal || !checkoutSession) return null;

  const membershipDiscount = calculatedTotal.scheduleDiscount ?? 0;

  return (
    <div>
      {calculatedTotal.couponDiscount > 0 ? (
        <div className="flex justify-between text-lg font-medium pt-4 border-t border-gray-100 pb-2">
          <span className="text-sm text-gray-600">{t(msg.cart.couponDiscount)}</span>
          <span className="text-sm font-medium">
            - {formatCurrency(calculatedTotal.couponDiscount, calculatedTotal.currency)}
          </span>
        </div>
      ) : null}

      {membershipDiscount > 0 ? (
        <div className="flex justify-between text-lg pt-4 border-t border-gray-100 pb-2">
          <span className="text-sm text-gray-600">
            {t(msg.cart.membershipDiscountYouEarned).replace(
              "%membershipDiscount%",
              formatCurrency(membershipDiscount, calculatedTotal.currency) ?? ""
            )}
          </span>
        </div>
      ) : null}

      {checkoutCore.sumNonExcludedGiftCards(checkoutSession, "guru-default") > 0 ? (
        <div className="flex justify-between items-center mb-2">
          {/* This is called coupon despite it being giftcard for UI purposes */}
          <span className="text-sm text-gray-600">{t(msg.checkout.couponUsed)}</span>
          <span className="text-sm font-medium">
            - {formatCurrency(checkoutCore.sumNonExcludedGiftCards(checkoutSession, "guru-default"), calculatedTotal.currency)}
          </span>
        </div>
      ) : null}

      {checkoutCore.sumGuruSollarUsed(checkoutSession, "guru-default") > 0 ? (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">{t(msg.cart.gurudollarUsed)}</span>
          <span className="text-sm font-medium">
            - {formatCurrency(checkoutCore.sumGuruSollarUsed(checkoutSession, "guru-default"), calculatedTotal.currency)}
          </span>
        </div>
      ) : null}

      {calculatedTotal.recycleDepositTotal && calculatedTotal.recycleDepositTotal > 0 ? (
        <div className="flex justify-between text-base">
          <span className="text-gray-700">{t(msg.cart.recycleDeposit)}</span>
          <span className="font-medium">{formatCurrency(calculatedTotal.recycleDepositTotal, calculatedTotal.currency)}</span>
        </div>
      ) : null}

      {calculatedTotal.orderRewardPoints > 0 ? (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">{t(msg.checkout.earnPoint)}</span>
          <span className="test-sm font-medium">
            {calculatedTotal.orderRewardPoints} {t(msg.checkout.point)}
          </span>
        </div>
      ) : null}

      <PointRedeemSummaryRow pointRedeemRequired={calculatedTotal.pointRedeemRequired} />
    </div>
  );
}

function convertCartToFormData(cart: Cart): CartFormData {
  return {
    items: cart.items.map((i) => {
      const attachToIdx = i.attachCartItemId ? cart.items.findIndex((ci) => ci.id === i.attachCartItemId) : undefined;

      return {
        id: i.id,
        productId: i.product.id,
        quantity: i.quantity,
        price: i.itemPrice,
        discountPrice: i.discountPrice,
        selected: true,
        isPointRedeemed: i.isPointRedeemed,
        addOnDiscountId: i.addOnDiscount?.id,
        attachToIdx: attachToIdx != null && attachToIdx >= 0 ? attachToIdx : undefined,
      };
    }),
  };
}
